import { useState, useEffect, useMemo } from "react";
import { Icon, Avatar } from "./Common.jsx";
import { fmt } from "../data.js";
import { useApp } from "../store.jsx";
import {
  createManager,
  createEmployee,
  bulkCreateEmployees,
  submitReport,
  getReportDraft,
  createTask,
  linkProjectTasks,
  listTeam,
  listDepartments,
  listEmployees,
  getMe,
  createDepartment,
  updateDepartment,
  deleteDepartment,
  updateReport,
} from "../lib/api";
import PhoneInput from "./PhoneInput.jsx";
import { detectDefaultCountry } from "../lib/timezone-country";
import { useRole } from "../hooks/useRole";

const EDIT_STATUS_OPTIONS = [
  { value: "done", label: "Done", color: "var(--success, #22c55e)" },
  { value: "in_process", label: "In Process", color: "var(--warning, #f59e0b)" },
  { value: "pending", label: "Pending", color: "var(--error, #ef4444)" },
];

export function ReportModal({ report, onClose, onSaved }) {
  const { toast } = useApp();
  const r = report;
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editErr, setEditErr] = useState("");
  const isToday = r.createdAt && new Date(r.createdAt).toDateString() === new Date().toDateString();
  const [editMsg, setEditMsg] = useState(r.message || "");
  const [editDate, setEditDate] = useState(() => { const d = new Date(r.date); return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10); });
  const [editLink, setEditLink] = useState(r.link || "");
  // Only reportTasks carry a taskId — the legacy single-message fallback can't
  // be status-edited, so it just won't render task rows in the editor.
  const [editItems, setEditItems] = useState(() =>
    r.tasks
      .filter((t) => t.taskId)
      .map((t) => ({
        taskId: t.taskId,
        title: t.text,
        status: t.status === "in-progress" ? "in_process" : t.status,
        note: t.note || "",
        progressPct: t.progressPct ?? null,
      })),
  );
  const setEditItem = (taskId, patch) =>
    setEditItems((its) =>
      its.map((it) => (it.taskId === taskId ? { ...it, ...patch } : it)),
    );

  const handleSaveEdit = async () => {
    for (const it of editItems) {
      if (!it.status) {
        setEditErr(`Pick a status for "${it.title}"`);
        return;
      }
      if (it.status === "pending" && !it.note.trim()) {
        setEditErr(`Add a blocker for "${it.title}"`);
        return;
      }
    }
    setSaving(true);
    setEditErr("");
    try {
      const payload = {
        message: editMsg,
        date: editDate || undefined,
        link: editLink,
      };
      if (editItems.length) {
        payload.items = editItems.map((it) => ({
          taskId: it.taskId,
          status: it.status,
          note:
            it.status === "pending"
              ? it.note.trim()
              : it.note.trim() || undefined,
          progressPct:
            it.status === "in_process" && it.progressPct != null
              ? Number(it.progressPct)
              : undefined,
        }));
      }
      await updateReport(r.id, payload);
      toast("Report updated", "success");
      if (onSaved) onSaved();
      else setEditing(false);
    } catch (err) {
      setEditErr(err.message || "Failed to update report");
      toast(err.message || "Failed to update report", "error");
    } finally {
      setSaving(false);
    }
  };

  const doneCount = r.tasks.filter((t) => t.status === "done").length;
  const totalCount = r.tasks.length;
  const completionPct = totalCount ? Math.round((doneCount / totalCount) * 100) : 0;

  const statusMeta = {
    done: {
      color: "var(--success, #22c55e)",
      icon: "check-circle-2",
      badge: "badge-success",
      label: "done",
    },
    in_process: {
      color: "var(--warning, #f59e0b)",
      icon: "clock",
      badge: "badge-muted",
      label: "in process",
    },
    "in-progress": {
      color: "var(--warning, #f59e0b)",
      icon: "clock",
      badge: "badge-muted",
      label: "in progress",
    },
    pending: {
      color: "var(--error, #ef4444)",
      icon: "alert-circle",
      badge: "badge-error",
      label: "pending",
    },
  };

  const sectionTitle = {
    fontSize: 12.5,
    fontWeight: 700,
    color: "var(--text-muted)",
    textTransform: "uppercase",
    letterSpacing: 0.6,
    margin: "20px 0 10px",
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        maxHeight: "85vh",
        minHeight: 0,
      }}
    >
      <div
        className="modal-head"
        style={{ flexShrink: 0, position: "sticky", top: 0, zIndex: 2 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar
            name={r.employee?.name || ""}
            size="lg"
            seed={(r.employee?.id || "").length}
          />
          <div>
            <div style={{ fontFamily: "Inter", fontSize: 17, fontWeight: 700 }}>
              {r.employee?.name || ""}
            </div>
            <div
              style={{
                fontSize: 12.5,
                color: "var(--text-muted)",
                marginTop: 2,
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <span>{r.employee?.department || ""}</span>
              <span style={{ opacity: 0.4 }}>·</span>
              <span>{fmt.date(r.date)}</span>
              <span
                className="badge badge-muted"
                style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
              >
                <Icon
                  name={r.source === "whatsapp" ? "message-circle" : "globe"}
                  size={11}
                />
                {r.source === "whatsapp" ? "WhatsApp" : "Web"}
              </span>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {!editing && isToday && (
            <button className="icon-btn" title="Edit" onClick={() => setEditing(true)}>
              <Icon name="edit-2" size={14} />
            </button>
          )}
          <button className="icon-btn" onClick={onClose}>
            <Icon name="x" size={16} />
          </button>
        </div>
      </div>

      <div
        className="modal-body"
        style={{ flex: 1, minHeight: 0, overflowY: "auto" }}
      >
        {editing && (
          <div style={{ marginBottom: 14, padding: 14, borderRadius: 10, border: "1px solid var(--primary)", background: "rgba(99,102,241,.05)" }}>
            <div className="field" style={{ marginBottom: 10 }}>
              <label>Date</label>
              <input type="date" className="input" value={editDate} onChange={(e) => setEditDate(e.target.value)} />
            </div>

            {editItems.length > 0 && (
              <div className="field" style={{ marginBottom: 10 }}>
                <label>Tasks ({editItems.length})</label>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {editItems.map((it, idx) => {
                    const statusColor =
                      EDIT_STATUS_OPTIONS.find((o) => o.value === it.status)?.color ||
                      "var(--border)";
                    return (
                      <div
                        key={it.taskId}
                        style={{
                          padding: 12,
                          paddingLeft: 14,
                          borderRadius: 10,
                          background: "var(--surface)",
                          border: "1px solid var(--border)",
                          borderLeft: `3px solid ${statusColor}`,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
                          <div
                            style={{
                              flexShrink: 0,
                              minWidth: 28,
                              height: 26,
                              padding: "0 8px",
                              borderRadius: 6,
                              background: "var(--surface-2)",
                              border: "1px solid var(--border)",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 11.5,
                              fontWeight: 700,
                              color: "var(--text-muted)",
                            }}
                          >
                            Task {idx + 1}
                          </div>
                          <div style={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: 13.5, marginTop: 3 }}>
                            {it.title}
                          </div>
                        </div>

                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {EDIT_STATUS_OPTIONS.map((opt) => {
                            const active = it.status === opt.value;
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => setEditItem(it.taskId, { status: opt.value })}
                                style={{
                                  padding: "6px 12px",
                                  borderRadius: 999,
                                  border: `1px solid ${active ? opt.color : "var(--border)"}`,
                                  background: active ? opt.color : "transparent",
                                  color: active ? "#fff" : "var(--text-muted)",
                                  fontSize: 12.5,
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  transition: "all .15s ease",
                                }}
                              >
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>

                        {it.status === "pending" && (
                          <input
                            className="input"
                            placeholder="Describe the blocker (required)"
                            value={it.note}
                            onChange={(e) => setEditItem(it.taskId, { note: e.target.value })}
                            style={{ marginTop: 10, borderColor: "var(--error)" }}
                          />
                        )}
                        {it.status === "in_process" && (
                          <input
                            className="input"
                            type="number"
                            min={0}
                            max={100}
                            placeholder="Progress % (optional)"
                            value={it.progressPct ?? ""}
                            onChange={(e) =>
                              setEditItem(it.taskId, {
                                progressPct: e.target.value === "" ? null : Number(e.target.value),
                              })
                            }
                            style={{ marginTop: 10, maxWidth: 220 }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="field" style={{ marginBottom: 10 }}>
              <label>Message</label>
              <textarea className="input" rows={4} value={editMsg} onChange={(e) => setEditMsg(e.target.value)} style={{ resize: "vertical" }} />
            </div>
            <div className="field" style={{ marginBottom: 10 }}>
              <label>Link</label>
              <input type="text" className="input" value={editLink} onChange={(e) => setEditLink(e.target.value)} placeholder="https://… (separate multiple links with spaces)" />
            </div>
            {editErr && (
              <div style={{ color: "var(--error)", fontSize: 12.5, marginBottom: 10 }}>{editErr}</div>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn btn-ghost btn-sm" disabled={saving} onClick={() => { setEditing(false); setEditErr(""); }}>Cancel</button>
              <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleSaveEdit}>
                <Icon name="check" size={14} /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 10,
            marginBottom: 14,
          }}
        >
          <div
            style={{
              padding: 14,
              borderRadius: 10,
              border: "1px solid var(--border)",
              background: "var(--surface-2)",
            }}
          >
            <div
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                color: "var(--text-muted)",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Tasks done
            </div>
            <div
              style={{
                marginTop: 6,
                display: "flex",
                alignItems: "baseline",
                gap: 6,
              }}
            >
              <span style={{ fontSize: 22, fontWeight: 700 }}>{doneCount}</span>
              <span style={{ color: "var(--text-muted)", fontSize: 14 }}>
                / {totalCount}
              </span>
            </div>
            <div
              style={{
                marginTop: 10,
                height: 6,
                borderRadius: 999,
                background: "var(--border)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${completionPct}%`,
                  background: "var(--success, #22c55e)",
                  transition: "width .3s ease",
                }}
              />
            </div>
          </div>
          <div
            style={{
              padding: 14,
              borderRadius: 10,
              border: "1px solid var(--border)",
              background: "var(--surface-2)",
            }}
          >
            <div
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                color: "var(--text-muted)",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Sentiment
            </div>
            <div style={{ marginTop: 10 }}>
              {r.sentiment === "high" ? (
                <span className="badge badge-success">Positive</span>
              ) : r.sentiment === "low" ? (
                <span className="badge badge-error">At risk</span>
              ) : (
                <span className="badge badge-muted">Neutral</span>
              )}
            </div>
          </div>
        </div>

        {r.aiSummary && (
          <div
            style={{
              padding: 14,
              borderRadius: 10,
              border: "1px solid var(--border)",
              background: "var(--surface-2)",
              display: "flex",
              gap: 10,
              alignItems: "flex-start",
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: "rgba(99,102,241,.15)",
                color: "var(--primary, #6366f1)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Icon name="sparkles" size={14} />
            </div>
            <div>
              <div
                style={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: "var(--text-muted)",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  marginBottom: 4,
                }}
              >
                AI Summary
              </div>
              <div style={{ fontSize: 13.5, lineHeight: 1.5 }}>
                {r.aiSummary}
              </div>
            </div>
          </div>
        )}

        <h4 style={sectionTitle}>Tasks ({r.tasks.length})</h4>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {r.tasks.map((t, i) => {
            const meta = statusMeta[t.status] || statusMeta.pending;
            return (
              <div
                key={i}
                style={{
                  padding: 12,
                  paddingLeft: 14,
                  borderRadius: 10,
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  borderLeft: `3px solid ${meta.color}`,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                }}
              >
                <div
                  style={{
                    flexShrink: 0,
                    minWidth: 28,
                    height: 26,
                    padding: "0 8px",
                    borderRadius: 6,
                    background: "var(--surface-2)",
                    border: "1px solid var(--border)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11.5,
                    fontWeight: 700,
                    color: "var(--text-muted)",
                  }}
                >
                  Task {i + 1}
                </div>
                <div style={{ color: meta.color, marginTop: 4 }}>
                  <Icon name={meta.icon} size={16} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      flexWrap: "wrap",
                    }}
                  >
                    <span style={{ fontWeight: 600, fontSize: 13.5 }}>
                      {t.text}
                    </span>
                    <span className={`badge ${meta.badge}`}>{meta.label}</span>
                    {t.source === "self_added" && (
                      <span className="badge badge-muted">self</span>
                    )}
                    {t.carried && (
                      <span
                        title="Carried from yesterday"
                        className="badge badge-muted"
                      >
                        ↻ carried
                      </span>
                    )}
                  </div>
                  {t.note && (
                    <div
                      style={{
                        fontSize: 12.5,
                        color: "var(--text-muted)",
                        marginTop: 6,
                        lineHeight: 1.45,
                      }}
                    >
                      {t.note}
                    </div>
                  )}
                  {(t.assignedAt || t.submittedAt) && (
                    <div
                      style={{
                        display: "flex",
                        gap: 14,
                        flexWrap: "wrap",
                        marginTop: 8,
                        fontSize: 11.5,
                        color: "var(--text-muted)",
                      }}
                    >
                      {t.assignedAt && (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <Icon name="user-plus" size={11} /> Assigned:{" "}
                          {fmt.dateTime(t.assignedAt)}
                        </span>
                      )}
                      {t.submittedAt && (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <Icon name="send" size={11} /> Submitted:{" "}
                          {fmt.dateTime(t.submittedAt)}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {r.blockers.length > 0 && (
          <>
            <h4 style={{ ...sectionTitle, color: "var(--error)" }}>
              Blockers ({r.blockers.length})
            </h4>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {r.blockers.map((b, i) => {
                const isObj = b && typeof b === "object";
                const text = isObj
                  ? b.description || b.text || b.message || JSON.stringify(b)
                  : String(b);
                const category = isObj ? b.category : null;
                return (
                <div
                  key={i}
                  style={{
                    padding: 12,
                    borderRadius: 10,
                    background: "rgba(239,68,68,.08)",
                    border: "1px solid rgba(239,68,68,.3)",
                    borderLeft: "3px solid var(--error)",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                  }}
                >
                  <div style={{ color: "var(--error)", marginTop: 2 }}>
                    <Icon name="alert-triangle" size={16} />
                  </div>
                  <div style={{ fontSize: 13.5, lineHeight: 1.45, flex: 1 }}>
                    {category && (
                      <span
                        className="badge badge-error"
                        style={{ marginRight: 8, textTransform: "capitalize" }}
                      >
                        {category}
                      </span>
                    )}
                    {text}
                  </div>
                </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <div
        className="modal-foot"
        style={{ flexShrink: 0, position: "sticky", bottom: 0, zIndex: 2 }}
      >
        <button className="btn btn-ghost" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

export function AddManagerModal({ onClose, onSubmit }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "", // full E.164 from PhoneInput
    department: "",
  });
  const [phoneValid, setPhoneValid] = useState(false);
  const [detectedCountry] = useState(() => detectDefaultCountry());
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [depts, setDepts] = useState([]);

  useEffect(() => {
    listDepartments()
      .then(setDepts)
      .catch(() => {});
  }, []);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: null }));
    if (serverError) setServerError("");
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email";
    if (!form.phone.trim()) e.phone = "Phone is required";
    else if (!phoneValid) e.phone = "Enter a valid phone number";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setServerError("");
    try {
      const result = await createManager({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        department: form.department.trim() || undefined,
      });
      onSubmit && onSubmit(result);
      onClose();
    } catch (err) {
      if (err.status === 409)
        setServerError(
          "A user with this email already exists in your company.",
        );
      else if (err.status === 401)
        setServerError("You are not signed in. Please sign in again.");
      else if (err.status === 403)
        setServerError("Only admins can invite managers.");
      else if (err.status === 400)
        setServerError("Invalid input — please check the fields.");
      else setServerError(err.message || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const fieldStyle = { display: "flex", flexDirection: "column", gap: 6 };
  const labelStyle = {
    fontSize: 12.5,
    fontWeight: 600,
    color: "var(--text-muted)",
  };
  const inputStyle = (hasError) => ({
    background: "var(--surface-2)",
    border: `1px solid ${hasError ? "var(--error)" : "var(--border)"}`,
    borderRadius: 8,
    padding: "10px 12px",
    color: "var(--text)",
    fontSize: 14,
    outline: "none",
    fontFamily: "inherit",
  });
  const errStyle = { fontSize: 11.5, color: "var(--error)" };

  return (
    <form onSubmit={handleSubmit}>
      <div className="modal-head">
        <h3>Add Manager</h3>
        <button
          type="button"
          className="icon-btn"
          onClick={onClose}
          disabled={submitting}
        >
          <Icon name="x" size={16} />
        </button>
      </div>
      <div
        className="modal-body"
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        {serverError && (
          <div
            style={{
              background: "rgba(239,68,68,.1)",
              border: "1px solid var(--error)",
              color: "var(--error)",
              padding: "10px 12px",
              borderRadius: 8,
              fontSize: 13,
            }}
          >
            {serverError}
          </div>
        )}
        <div style={fieldStyle}>
          <label style={labelStyle}>Name</label>
          <input
            style={inputStyle(errors.name)}
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="e.g. Priya Sharma"
            autoFocus
            disabled={submitting}
          />
          {errors.name && <span style={errStyle}>{errors.name}</span>}
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Email</label>
          <input
            type="email"
            style={inputStyle(errors.email)}
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="priya@company.com"
            disabled={submitting}
          />
          {errors.email && <span style={errStyle}>{errors.email}</span>}
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Phone</label>
          <PhoneInput
            defaultCountry={detectedCountry}
            error={errors.phone}
            disabled={submitting}
            wrapperStyle={{ marginBottom: 0 }}
            onChange={({ e164, isValid }) => {
              set("phone", e164);
              setPhoneValid(isValid);
            }}
          />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Department (optional)</label>
          <select
            style={inputStyle(errors.department)}
            value={form.department}
            onChange={(e) => set("department", e.target.value)}
            disabled={submitting}
          >
            <option value="">— Assign later —</option>
            {depts.map((d) => (
              <option key={d.id} value={d.name}>
                {d.name}
              </option>
            ))}
          </select>
          {depts.length === 0 && (
            <span style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
              No departments yet — leave empty and assign one later from the
              Departments page.
            </span>
          )}
        </div>
      </div>
      <div className="modal-foot">
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? (
            <>Sending...</>
          ) : (
            <>
              <Icon name="user-plus" size={16} /> Send Invitation
            </>
          )}
        </button>
      </div>
    </form>
  );
}

export function AddEmployeeModal({ onClose, onSubmit }) {
  const role = useRole();
  const isManager = role === "manager";
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "", // full E.164 from PhoneInput
    department: "",
  });
  const [phoneValid, setPhoneValid] = useState(false);
  const [detectedCountry] = useState(() => detectDefaultCountry());
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [depts, setDepts] = useState([]);
  const [myDept, setMyDept] = useState("");

  useEffect(() => {
    if (!isManager) {
      listDepartments()
        .then(setDepts)
        .catch(() => {});
    }
  }, [isManager]);

  useEffect(() => {
    if (!isManager) return;
    getMe()
      .then((me) => {
        const d = me?.user?.department || me?.department || "";
        if (d) {
          setMyDept(d);
          setForm((f) => ({ ...f, department: d }));
        }
      })
      .catch(() => {});
  }, [isManager]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: null }));
    if (serverError) setServerError("");
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email";
    if (!form.phone.trim()) e.phone = "Phone is required";
    else if (!phoneValid) e.phone = "Enter a valid phone number";
    if (!form.department.trim()) e.department = "Department is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setServerError("");
    try {
      const result = await createEmployee({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        department: form.department.trim(),
      });
      onSubmit && onSubmit(result);
      onClose();
    } catch (err) {
      if (err.status === 409)
        setServerError(
          "A user with this email already exists in your company.",
        );
      else if (err.status === 401)
        setServerError("You are not signed in. Please sign in again.");
      else if (err.status === 403)
        setServerError("You do not have permission to invite employees.");
      else if (err.status === 400)
        setServerError("Invalid input — please check the fields.");
      else setServerError(err.message || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const fieldStyle = { display: "flex", flexDirection: "column", gap: 6 };
  const labelStyle = {
    fontSize: 12.5,
    fontWeight: 600,
    color: "var(--text-muted)",
  };
  const inputStyle = (hasError) => ({
    background: "var(--surface-2)",
    border: `1px solid ${hasError ? "var(--error)" : "var(--border)"}`,
    borderRadius: 8,
    padding: "10px 12px",
    color: "var(--text)",
    fontSize: 14,
    outline: "none",
    fontFamily: "inherit",
  });
  const errStyle = { fontSize: 11.5, color: "var(--error)" };

  return (
    <form onSubmit={handleSubmit}>
      <div className="modal-head">
        <h3>Invite Employee</h3>
        <button
          type="button"
          className="icon-btn"
          onClick={onClose}
          disabled={submitting}
        >
          <Icon name="x" size={16} />
        </button>
      </div>
      <div
        className="modal-body"
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        {serverError && (
          <div
            style={{
              background: "rgba(239,68,68,.1)",
              border: "1px solid var(--error)",
              color: "var(--error)",
              padding: "10px 12px",
              borderRadius: 8,
              fontSize: 13,
            }}
          >
            {serverError}
          </div>
        )}
        <div style={fieldStyle}>
          <label style={labelStyle}>Name</label>
          <input
            style={inputStyle(errors.name)}
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="e.g. Rahul Verma"
            autoFocus
            disabled={submitting}
          />
          {errors.name && <span style={errStyle}>{errors.name}</span>}
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Email</label>
          <input
            type="email"
            style={inputStyle(errors.email)}
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="rahul@company.com"
            disabled={submitting}
          />
          {errors.email && <span style={errStyle}>{errors.email}</span>}
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Phone</label>
          <PhoneInput
            defaultCountry={detectedCountry}
            error={errors.phone}
            disabled={submitting}
            wrapperStyle={{ marginBottom: 0 }}
            onChange={({ e164, isValid }) => {
              set("phone", e164);
              setPhoneValid(isValid);
            }}
          />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Department</label>
          {isManager ? (
            <input
              style={{ ...inputStyle(false), opacity: 0.7 }}
              value={myDept || "(no department assigned)"}
              readOnly
              disabled
            />
          ) : (
            <select
              style={inputStyle(errors.department)}
              value={form.department}
              onChange={(e) => set("department", e.target.value)}
              disabled={submitting}
            >
              <option value="">— Select department —</option>
              {depts.map((d) => (
                <option key={d.id} value={d.name}>
                  {d.name}
                </option>
              ))}
            </select>
          )}
          {errors.department && (
            <span style={errStyle}>{errors.department}</span>
          )}
          {!isManager && depts.length === 0 && (
            <span style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
              No departments yet — create one from the Departments page first.
            </span>
          )}
          {isManager && !myDept && (
            <span style={{ fontSize: 11.5, color: "var(--error)" }}>
              Your account isn't assigned to a department yet — ask your admin.
            </span>
          )}
        </div>
      </div>
      <div className="modal-foot">
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={submitting || (isManager && !myDept)}
        >
          {submitting ? (
            <>Sending...</>
          ) : (
            <>
              <Icon name="user-plus" size={16} /> Send Invitation
            </>
          )}
        </button>
      </div>
    </form>
  );
}

export function BulkInviteModal({ onClose, onSubmit, toast: showToast }) {
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [apiError, setApiError] = useState("");

  const parseLines = (input) => {
    return input
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => {
        const parts = l.split(",").map((p) => p.trim());
        if (parts.length < 4) return null;
        return { name: parts[0], email: parts[1], phone: parts[2], department: parts[3] };
      })
      .filter(Boolean);
  };

  const preview = parseLines(text);

  const handleSubmit = async () => {
    if (!preview.length) return;
    setSubmitting(true);
    setApiError("");
    try {
      const result = await bulkCreateEmployees({ employees: preview });
      setResults(result);
      if (result.results.every((r) => r.success)) {
        onSubmit && onSubmit(result);
      }
    } catch (err) {
      const msg = err.message || "Bulk invite failed";
      if (showToast) showToast(msg, "error");
      else setApiError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const formatExample = `Rahul Verma, rahul@company.com, +919876543210, Engineering
Priya Sharma, priya@company.com, +919876543211, Marketing`;

  return (
    <>
      <div className="modal-head">
        <h3>{results ? "Invite Results" : "Bulk Invite Employees"}</h3>
        <button className="icon-btn" onClick={onClose}>
          <Icon name="x" size={16} />
        </button>
      </div>
      <div className="modal-body" style={{ minHeight: 200 }}>
        {!results ? (
          <>
            {apiError && (
              <div style={{ background: "rgba(239,68,68,.1)", border: "1px solid var(--error)", color: "var(--error)", padding: "10px 12px", borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
                {apiError}
              </div>
            )}
            <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 12 }}>
              Enter one employee per line: <strong>Name, Email, Phone, Department</strong>
            </p>
            <textarea
              className="input"
              rows={8}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={formatExample}
              style={{ resize: "vertical", fontFamily: "monospace", fontSize: 13 }}
              disabled={submitting}
            />
            {preview.length > 0 && (
              <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 8 }}>
                {preview.length} employee(s) parsed
              </p>
            )}
          </>
        ) : (
          <div style={{ fontSize: 13 }}>
            <p style={{ fontWeight: 600, marginBottom: 10, color: results.results.every((r) => r.success) ? "var(--success)" : "var(--text)" }}>
              {results.message}
            </p>
            <div style={{ maxHeight: 300, overflowY: "auto" }}>
              {results.results.map((r, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 0",
                    borderBottom: "1px solid var(--border)",
                    fontSize: 12.5,
                  }}
                >
                  <span style={{ color: r.success ? "var(--success)" : "var(--error)", fontWeight: 600 }}>
                    {r.success ? "✓" : "✗"}
                  </span>
                  <span style={{ flex: 1 }}>{r.email}</span>
                  {!r.success && <span style={{ color: "var(--error)", fontSize: 11.5 }}>{r.error}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="modal-foot">
        <button className="btn btn-ghost" onClick={onClose}>
          {results ? "Close" : "Cancel"}
        </button>
        {!results && (
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={submitting || preview.length === 0}
          >
            {submitting ? "Sending..." : <><Icon name="user-plus" size={16} /> Invite {preview.length} employee(s)</>}
          </button>
        )}
      </div>
    </>
  );
}

export function SubmitReportModal({ onClose, onSubmit }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]); // { task, suggestedStatus, previousNote, carriedFromReportTaskId, status, note, progressPct }
  const [freeText, setFreeText] = useState("");
  const [link, setLink] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [openSection, setOpenSection] = useState({
    assigned: true,
    self: true,
  });

  useEffect(() => {
    getReportDraft()
      .then(({ items }) => {
        setItems(
          items.map((it) => ({
            ...it,
            status: it.suggestedStatus || "",
            note: it.previousNote || "",
            progressPct: null,
          })),
        );
      })
      .catch((e) => setServerError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const setItem = (taskId, patch) =>
    setItems((its) =>
      its.map((it) => (it.task.id === taskId ? { ...it, ...patch } : it)),
    );

  const validate = () => {
    if (!items.length)
      return "You have no active tasks. Add one from My Tasks first.";
    const touched = items.filter((it) => it.status);
    if (!touched.length) return "Pick a status for at least one task.";
    for (const it of touched) {
      if (it.status === "pending" && !it.note.trim()) {
        return `Add a blocker for "${it.task.title}"`;
      }
    }
    return null;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const err = validate();
    if (err) {
      setServerError(err);
      return;
    }
    setSubmitting(true);
    setServerError("");
    try {
      const payload = {
        items: items
          .filter((it) => it.status)
          .map((it) => ({
            taskId: it.task.id,
            status: it.status,
            note:
              it.status === "pending"
                ? it.note.trim()
                : it.note.trim() || undefined,
            progressPct:
              it.status === "in_process" && it.progressPct != null
                ? Number(it.progressPct)
                : undefined,
            carriedFromReportTaskId: it.carriedFromReportTaskId || undefined,
          })),
        freeText: freeText.trim() || undefined,
        link: link.trim() || undefined,
        source: "web",
      };
      const result = await submitReport(payload);
      onSubmit && onSubmit(result);
      onClose();
    } catch (e) {
      setServerError(e.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const assigned = items.filter((it) => it.task.source === "assigned");
  const self = items.filter((it) => it.task.source === "self_added");

  const labelStyle = {
    fontSize: 12.5,
    fontWeight: 600,
    color: "var(--text-muted)",
    letterSpacing: 0.2,
  };
  const inputStyle = {
    width: "100%",
    background: "var(--surface-2)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: "10px 12px",
    color: "var(--text)",
    fontSize: 14,
    outline: "none",
    fontFamily: "inherit",
    transition: "border-color .15s ease",
  };

  const STATUS_OPTIONS = [
    { value: "done", label: "Done", color: "var(--success, #22c55e)" },
    { value: "in_process", label: "In Process", color: "var(--warning, #f59e0b)" },
    { value: "pending", label: "Pending", color: "var(--error, #ef4444)" },
  ];

  const renderRow = (it, idx) => {
    const statusColor =
      STATUS_OPTIONS.find((o) => o.value === it.status)?.color ||
      "var(--border)";
    return (
    <div
      key={it.task.id}
      style={{
        padding: "14px",
        paddingLeft: 16,
        borderRadius: 10,
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderLeft: `3px solid ${statusColor}`,
        marginBottom: 8,
        transition: "border-color .15s ease",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          marginBottom: it.status ? 10 : 0,
        }}
      >
        <div
          style={{
            flexShrink: 0,
            minWidth: 28,
            height: 28,
            padding: "0 8px",
            borderRadius: 6,
            background: "var(--surface-2)",
            border: "1px solid var(--border)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 700,
            color: "var(--text-muted)",
            letterSpacing: 0.3,
          }}
        >
          Task {idx + 1}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: 14,
              color: "var(--text)",
              lineHeight: 1.35,
            }}
          >
            {it.task.title}
          </div>
          {it.carriedFromReportTaskId && (
            <span
              title="Carried from yesterday"
              className="badge badge-muted"
              style={{ marginTop: 6, display: "inline-flex", gap: 4 }}
            >
              ↻ carried
            </span>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {STATUS_OPTIONS.map((opt) => {
          const active = it.status === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() =>
                setItem(it.task.id, {
                  status: active ? "" : opt.value,
                })
              }
              style={{
                padding: "6px 12px",
                borderRadius: 999,
                border: `1px solid ${active ? opt.color : "var(--border)"}`,
                background: active ? opt.color : "transparent",
                color: active ? "#fff" : "var(--text-muted)",
                fontSize: 12.5,
                fontWeight: 600,
                cursor: "pointer",
                transition: "all .15s ease",
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {it.status === "pending" && (
        <div style={{ marginTop: 10 }}>
          <input
            placeholder="Describe the blocker (required)"
            value={it.note}
            onChange={(e) => setItem(it.task.id, { note: e.target.value })}
            style={{
              ...inputStyle,
              borderColor: "var(--error)",
            }}
          />
        </div>
      )}
      {it.status === "in_process" && (
        <div style={{ marginTop: 10 }}>
          <input
            type="number"
            min={0}
            max={100}
            placeholder="Progress % (optional)"
            value={it.progressPct ?? ""}
            onChange={(e) =>
              setItem(it.task.id, {
                progressPct:
                  e.target.value === "" ? null : Number(e.target.value),
              })
            }
            style={{ ...inputStyle, maxWidth: 220 }}
          />
        </div>
      )}
    </div>
    );
  };

  const accordion = (key, label, iconName, list) => {
    const open = openSection[key];
    return (
      <div
        style={{
          marginBottom: 14,
          borderRadius: 10,
          border: "1px solid var(--border)",
          overflow: "hidden",
          background: "var(--surface-2)",
        }}
      >
        <button
          type="button"
          onClick={() => setOpenSection((s) => ({ ...s, [key]: !s[key] }))}
          style={{
            width: "100%",
            padding: "12px 14px",
            background: "transparent",
            border: "none",
            textAlign: "left",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            color: "var(--text)",
            fontFamily: "inherit",
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Icon name={iconName} size={16} />
            <span style={{ fontWeight: 600, fontSize: 13.5 }}>{label}</span>
            <span
              className="badge badge-muted"
              style={{ fontSize: 11 }}
            >
              {list.length}
            </span>
          </span>
          <Icon name={open ? "chevron-up" : "chevron-down"} size={16} />
        </button>
        {open && (
          <div style={{ padding: "10px 12px 12px", background: "var(--surface)" }}>
            {list.length === 0 ? (
              <div
                style={{
                  color: "var(--text-muted)",
                  fontSize: 13,
                  padding: "12px 4px",
                  textAlign: "center",
                }}
              >
                No tasks in this section.
              </div>
            ) : (
              list.map(renderRow)
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        display: "flex",
        flexDirection: "column",
        maxHeight: "85vh",
        minHeight: 0,
      }}
    >
      <div
        className="modal-head"
        style={{ flexShrink: 0, position: "sticky", top: 0, zIndex: 2 }}
      >
        <div>
          <h3 style={{ margin: 0 }}>Submit daily report</h3>
          <div
            style={{
              fontSize: 12.5,
              color: "var(--text-muted)",
              marginTop: 2,
            }}
          >
            Update your tasks and add any notes for today
          </div>
        </div>
        <button
          type="button"
          className="icon-btn"
          onClick={onClose}
          disabled={submitting}
        >
          <Icon name="x" size={16} />
        </button>
      </div>

      <div
        className="modal-body"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        {loading ? (
          <div
            style={{
              color: "var(--text-muted)",
              padding: "40px 0",
              textAlign: "center",
              fontSize: 14,
            }}
          >
            Loading your tasks…
          </div>
        ) : (
          <>
            {serverError && (
              <div
                style={{
                  background: "rgba(239,68,68,.1)",
                  border: "1px solid var(--error)",
                  color: "var(--error)",
                  padding: "10px 12px",
                  borderRadius: 8,
                  fontSize: 13,
                  marginBottom: 14,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Icon name="alert-circle" size={16} />
                <span>{serverError}</span>
              </div>
            )}

            {accordion("assigned", "Assigned by manager", "briefcase", assigned)}
            {accordion("self", "My tasks", "user", self)}

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                marginBottom: 14,
              }}
            >
              <label style={labelStyle}>Anything else? (optional)</label>
              <textarea
                rows={3}
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
                placeholder="Highlights, wins, things worth sharing…"
                style={{ ...inputStyle, resize: "vertical", minHeight: 72 }}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={labelStyle}>Link (optional)</label>
              <input
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="https://…"
                style={inputStyle}
              />
            </div>
          </>
        )}
      </div>

      <div
        className="modal-foot"
        style={{ flexShrink: 0, position: "sticky", bottom: 0, zIndex: 2 }}
      >
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={submitting || loading}
        >
          {submitting ? (
            <>Submitting…</>
          ) : (
            <>
              <Icon name="send" size={16} /> Submit report
            </>
          )}
        </button>
      </div>
    </form>
  );
}

export function AddSelfTaskModal({ userId, onClose, onSubmit }) {
  const [form, setForm] = useState({ title: "", description: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: null }));
    if (serverError) setServerError("");
  };

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = "Task title is required";
    else if (form.title.trim().length < 3)
      e.title = "Title must be at least 3 characters";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setServerError("");
    try {
      const result = await createTask({
        userId,
        title: form.title.trim(),
        description: form.description.trim() || undefined,
      });
      onSubmit && onSubmit(result);
      onClose();
    } catch (err) {
      if (err.status === 401)
        setServerError("You are not signed in. Please sign in again.");
      else if (err.status === 400)
        setServerError("Invalid input — please check the fields.");
      else setServerError(err.message || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const fieldStyle = { display: "flex", flexDirection: "column", gap: 6 };
  const labelStyle = {
    fontSize: 12.5,
    fontWeight: 600,
    color: "var(--text-muted)",
  };
  const inputStyle = (hasError) => ({
    background: "var(--surface-2)",
    border: `1px solid ${hasError ? "var(--error)" : "var(--border)"}`,
    borderRadius: 8,
    padding: "10px 12px",
    color: "var(--text)",
    fontSize: 14,
    outline: "none",
    fontFamily: "inherit",
    width: "100%",
  });
  const errStyle = { fontSize: 11.5, color: "var(--error)" };

  return (
    <form onSubmit={handleSubmit}>
      <div className="modal-head">
        <div>
          <h3 style={{ margin: 0 }}>Add a personal task</h3>
          <div
            style={{
              fontSize: 12.5,
              color: "var(--text-muted)",
              marginTop: 2,
            }}
          >
            Track something you're working on, alongside what your manager
            assigned.
          </div>
        </div>
        <button
          type="button"
          className="icon-btn"
          onClick={onClose}
          disabled={submitting}
        >
          <Icon name="x" size={16} />
        </button>
      </div>

      <div
        className="modal-body"
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        {serverError && (
          <div
            style={{
              background: "rgba(239,68,68,.1)",
              border: "1px solid var(--error)",
              color: "var(--error)",
              padding: "10px 12px",
              borderRadius: 8,
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Icon name="alert-circle" size={16} />
            <span>{serverError}</span>
          </div>
        )}

        <div style={fieldStyle}>
          <label style={labelStyle}>Task title</label>
          <input
            style={inputStyle(errors.title)}
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder="e.g. Review the onboarding checklist"
            autoFocus
            disabled={submitting}
          />
          {errors.title && <span style={errStyle}>{errors.title}</span>}
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>Description (optional)</label>
          <textarea
            rows={3}
            style={{ ...inputStyle(false), resize: "vertical", minHeight: 76 }}
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder="Any context, sub-steps, or links…"
            disabled={submitting}
          />
        </div>
      </div>

      <div className="modal-foot">
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={submitting}
        >
          {submitting ? (
            <>Adding…</>
          ) : (
            <>
              <Icon name="plus" size={16} /> Add task
            </>
          )}
        </button>
      </div>
    </form>
  );
}

export function AssignTaskModal({ departments = [], onClose, onSubmit, initialUserId, projectId }) {
  const { toast } = useApp();
  // Multiple employees can be picked — one task is created per selected member.
  const [selectedUserIds, setSelectedUserIds] = useState(
    () => new Set(initialUserId ? [initialUserId] : []),
  );
  const [form, setForm] = useState({
    title: "",
    description: "",
    priority: "low",
    reminderInterval: 60,
    reminderChannels: [],
  });
  const [memberSearch, setMemberSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [expandedDepts, setExpandedDepts] = useState(() => new Set());
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");

  // When a parent (e.g. the Projects detail page) opens the modal without
  // pre-loading the team directory, fetch it ourselves so the picker isn't empty.
  const parentSuppliedDepts = departments.length > 0;
  const [fetchedDepts, setFetchedDepts] = useState([]);
  const [loadingDepts, setLoadingDepts] = useState(false);
  const directory = parentSuppliedDepts ? departments : fetchedDepts;

  useEffect(() => {
    if (parentSuppliedDepts) return; // parent already supplied the directory
    let alive = true;
    setLoadingDepts(true);
    listTeam({ groupBy: "department" })
      .then((res) => {
        if (alive) setFetchedDepts(res.departments || []);
      })
      .catch(() => {
        if (alive) setFetchedDepts([]);
      })
      .finally(() => {
        if (alive) setLoadingDepts(false);
      });
    return () => {
      alive = false;
    };
  }, [parentSuppliedDepts]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: null }));
    if (serverError) setServerError("");
  };

  const toggleUser = (id) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    if (errors.userId) setErrors((e) => ({ ...e, userId: null }));
    if (serverError) setServerError("");
  };

  const query = memberSearch.trim().toLowerCase();

  const filteredDepts = useMemo(() => {
    return directory
      .map((dept) => {
        let users = dept.users;
        if (query)
          users = users.filter(
            (u) =>
              u.name.toLowerCase().includes(query) ||
              u.email.toLowerCase().includes(query),
          );
        if (roleFilter === "manager") users = users.filter((u) => u.role === "manager");
        else if (roleFilter === "employee") users = users.filter((u) => u.role === "employee");
        return { ...dept, users, count: users.length };
      })
      .filter((d) => d.users.length > 0);
  }, [directory, query, roleFilter]);

  const selectedUsers = useMemo(() => {
    if (!selectedUserIds.size) return [];
    const out = [];
    for (const d of directory)
      for (const u of d.users)
        if (selectedUserIds.has(u.id)) out.push(u);
    return out;
  }, [directory, selectedUserIds]);

  function toggleDeptExpand(name) {
    setExpandedDepts((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  const validate = () => {
    const e = {};
    if (!selectedUserIds.size) e.userId = "Choose at least one employee";
    if (!form.title.trim()) e.title = "Task title is required";
    else if (form.title.trim().length < 3)
      e.title = "Title must be at least 3 characters";
    if (form.priority === "high" && form.reminderChannels.length === 0)
      e.reminderChannels = "High priority requires at least one reminder channel";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setServerError("");
    try {
      const base = {
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        priority: form.priority,
      };
      if (form.priority !== "low" && form.reminderChannels.length > 0) {
        base.reminderInterval = form.reminderInterval;
        base.reminderChannels = form.reminderChannels;
      }
      // One task per selected employee. Settle all so a single failure doesn't
      // abort the rest, then report how many made it.
      const ids = [...selectedUserIds];
      const results = await Promise.allSettled(
        ids.map((uid) => createTask({ ...base, userId: uid })),
      );
      const created = results
        .filter((r) => r.status === "fulfilled")
        .map((r) => r.value.task);
      const failed = ids.length - created.length;

      if (created.length === 0) {
        throw (
          results.find((r) => r.status === "rejected")?.reason ||
          new Error("Could not assign task")
        );
      }
      if (projectId) {
        await linkProjectTasks(
          projectId,
          created.map((t) => t.id),
        ).catch(() => {});
      }
      if (failed > 0) {
        toast(`Assigned to ${created.length}, ${failed} couldn't be assigned`, "error");
      }
      onSubmit && onSubmit(created);
      onClose();
    } catch (err) {
      if (err.status === 401)
        setServerError("You are not signed in. Please sign in again.");
      else if (err.status === 403)
        setServerError("You can only assign tasks to your direct reports.");
      else if (err.status === 400)
        setServerError("Invalid input — please check the fields.");
      else setServerError(err.message || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const fieldStyle = { display: "flex", flexDirection: "column", gap: 6 };
  const labelStyle = { fontSize: 12.5, fontWeight: 600, color: "var(--text-muted)" };
  const inputStyle = (hasError) => ({
    background: "var(--surface-2)",
    border: `1px solid ${hasError ? "var(--error)" : "var(--border)"}`,
    borderRadius: 8,
    padding: "10px 12px",
    color: "var(--text)",
    fontSize: 14,
    outline: "none",
    fontFamily: "inherit",
    width: "100%",
    boxSizing: "border-box",
  });
  const errStyle = { fontSize: 11.5, color: "var(--error)" };

  return (
    <form onSubmit={handleSubmit}>
      <div className="modal-head">
        <div>
          <h3 style={{ margin: 0 }}>Assign a task</h3>
          <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 2 }}>
            Pick an employee and add what they should work on.
          </div>
        </div>
        <button type="button" className="icon-btn" onClick={onClose} disabled={submitting}>
          <Icon name="x" size={16} />
        </button>
      </div>

      <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {serverError && (
          <div style={{ background: "rgba(239,68,68,.1)", border: "1px solid var(--error)", color: "var(--error)", padding: "10px 12px", borderRadius: 8, fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
            <Icon name="alert-circle" size={16} />
            <span>{serverError}</span>
          </div>
        )}

        <div style={fieldStyle}>
          <label style={labelStyle}>Assign to{selectedUserIds.size > 1 ? ` (${selectedUserIds.size} selected)` : ""}</label>

          <div style={{ display: "flex", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 140, display: "flex", alignItems: "center", gap: 6, background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 8, padding: "6px 10px" }}>
              <Icon name="search" size={14} />
              <input
                type="text"
                placeholder="Search by name or email…"
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                style={{ background: "none", border: "none", outline: "none", color: "var(--text)", fontSize: 13, flex: 1, fontFamily: "inherit" }}
              />
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              {["all", "manager", "employee"].map((r) => (
                <button
                  key={r}
                  type="button"
                  className={`btn btn-sm ${roleFilter === r ? "btn-primary" : "btn-ghost"}`}
                  onClick={() => setRoleFilter(r)}
                  style={{ fontSize: 11, padding: "2px 8px" }}
                >
                  {r === "all" ? "All" : r + "s"}
                </button>
              ))}
            </div>
          </div>

          <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 8 }}>
            {filteredDepts.length === 0 ? (
              <div style={{ padding: 12, fontSize: 13, color: "var(--text-muted)" }}>
                {loadingDepts ? "Loading team…" : "No people found."}
              </div>
            ) : (
              filteredDepts.map((dept) => {
                const open = expandedDepts.has(dept.name);
                return (
                  <div key={dept.name} style={{ borderBottom: "1px solid var(--border)" }}>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 10px", cursor: "pointer" }}
                      onClick={() => toggleDeptExpand(dept.name)}
                    >
                      <Icon name={open ? "chevron-down" : "chevron-right"} size={12} />
                      <span style={{ flex: 1, fontWeight: 500, fontSize: 13 }}>{dept.name}</span>
                      <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{dept.count}</span>
                    </div>
                    {open &&
                      dept.users.map((u) => {
                        const isSelected = selectedUserIds.has(u.id);
                        return (
                          <div
                            key={u.id}
                            onClick={() => toggleUser(u.id)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "6px 10px 6px 28px",
                              cursor: "pointer",
                              background: isSelected ? "rgba(99,102,241,0.08)" : "transparent",
                            }}
                          >
                            <Avatar name={u.name} size="sm" seed={(u.id || "").length} />
                            <span style={{ flex: 1, fontSize: 13 }}>{u.name}</span>
                            <span style={{ fontSize: 11, color: "var(--text-muted)", display: "none", "@media (min-width:480px)": { display: "inline" } }}>
                              {u.email}
                            </span>
                            {u.role === "manager" && (
                              <span className="badge" style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3, background: "rgba(99,102,241,0.12)", color: "var(--primary)", border: "1px solid rgba(99,102,241,0.3)", padding: "1px 5px" }}>
                                Mgr
                              </span>
                            )}
                            {isSelected && <Icon name="check" size={14} style={{ color: "var(--primary)" }} />}
                          </div>
                        );
                      })}
                  </div>
                );
              })
            )}
          </div>

          {selectedUsers.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
              {selectedUsers.map((su) => (
                <div key={su.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", background: "rgba(99,102,241,0.08)", borderRadius: 8, fontSize: 13 }}>
                  <Avatar name={su.name} size="sm" seed={(su.id || "").length} />
                  <span style={{ fontWeight: 500 }}>{su.name}</span>
                  <button
                    type="button"
                    style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: 16, padding: 0, lineHeight: 1 }}
                    onClick={() => toggleUser(su.id)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          {errors.userId && <span style={errStyle}>{errors.userId}</span>}
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>Task title</label>
          <input style={inputStyle(errors.title)} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Wire up SSO for the new partner" disabled={submitting} />
          {errors.title && <span style={errStyle}>{errors.title}</span>}
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>Description (optional)</label>
          <textarea rows={3} style={{ ...inputStyle(false), resize: "vertical", minHeight: 76 }} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Context, acceptance criteria, links…" disabled={submitting} />
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>Priority</label>
          <div style={{ display: "flex", gap: 6 }}>
            {[
              ["low", "Low", "var(--text-muted)"],
              ["medium", "Medium", "#f59e0b"],
              ["high", "High", "#ef4444"],
            ].map(([val, label, color]) => (
              <button
                key={val}
                type="button"
                onClick={() => { set("priority", val); if (val === "low") set("reminderChannels", []); }}
                disabled={submitting}
                style={{
                  flex: 1, padding: "8px 0", borderRadius: 8, fontSize: 13, fontWeight: 600,
                  cursor: "pointer", transition: "all 0.15s",
                  border: form.priority === val ? `2px solid ${color}` : "1px solid var(--border)",
                  background: form.priority === val ? `${color}15` : "var(--surface-2)",
                  color: form.priority === val ? color : "var(--text-muted)",
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {form.priority !== "low" && (
          <>
            <div style={fieldStyle}>
              <label style={labelStyle}>Reminder interval (minutes)</label>
              <input type="number" min={15} max={240} step={5} style={inputStyle(false)} value={form.reminderInterval}
                onChange={(e) => set("reminderInterval", Math.max(15, Math.min(240, parseInt(e.target.value, 10) || 60)))}
                disabled={submitting}
              />
              <span style={{ fontSize: 11.5, color: "var(--text-muted)" }}>Employee will be reminded every {form.reminderInterval} min until evening</span>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Remind via</label>
              <div style={{ display: "flex", gap: 14 }}>
                {[["whatsapp", "WhatsApp"], ["email", "Email"]].map(([val, label]) => (
                  <label key={val} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                    <input type="checkbox" checked={form.reminderChannels.includes(val)}
                      onChange={(e) => { const next = e.target.checked ? [...form.reminderChannels, val] : form.reminderChannels.filter((c) => c !== val); set("reminderChannels", next); }}
                      disabled={submitting}
                    />
                    {label}
                  </label>
                ))}
              </div>
              {form.priority === "high" && form.reminderChannels.length === 0 && (
                <span style={{ fontSize: 11.5, color: "var(--error)" }}>High priority tasks require at least one reminder channel</span>
              )}
            </div>
          </>
        )}
      </div>

      <div className="modal-foot">
        <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? <>Assigning…</> : <><Icon name="plus" size={16} /> Assign task</>}
        </button>
      </div>
    </form>
  );
}

export function ImportModal({ onClose, onImport }) {
  return (
    <>
      <div className="modal-head">
        <h3>Import from CSV</h3>
        <button className="icon-btn" onClick={onClose}>
          <Icon name="x" size={16} />
        </button>
      </div>
      <div className="modal-body">
        <div
          style={{
            border: "2px dashed var(--border)",
            borderRadius: 10,
            padding: 36,
            textAlign: "center",
            background: "var(--surface-2)",
          }}
        >
          <Icon name="upload-cloud" size={36} className="" />
          <div style={{ fontWeight: 600, marginBottom: 4, marginTop: 10 }}>
            Drop your CSV file here
          </div>
          <div
            style={{
              fontSize: 13,
              color: "var(--text-muted)",
              marginBottom: 14,
            }}
          >
            or click to browse · Max 10MB
          </div>
          <button className="btn btn-ghost btn-sm">Choose file</button>
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn btn-ghost" onClick={onClose}>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={() => {
            onImport && onImport();
            onClose();
          }}
        >
          <Icon name="check" size={16} /> Import
        </button>
      </div>
    </>
  );
}

// ===================== Department modals =====================

const deptFieldStyle = { display: "flex", flexDirection: "column", gap: 6 };
const deptLabelStyle = {
  fontSize: 12.5,
  fontWeight: 600,
  color: "var(--text-muted)",
};
const deptInputStyle = (hasError) => ({
  background: "var(--surface-2)",
  border: `1px solid ${hasError ? "var(--error)" : "var(--border)"}`,
  borderRadius: 8,
  padding: "10px 12px",
  color: "var(--text)",
  fontSize: 14,
  outline: "none",
  fontFamily: "inherit",
});
function DepartmentForm({
  initial,
  focus,
  onClose,
  onSaved,
  submitLabel,
  apiCall,
}) {
  const [name, setName] = useState(initial?.name || "");
  const [managerId, setManagerId] = useState(initial?.manager?.id || "");
  const [managers, setManagers] = useState([]);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    listEmployees()
      .then(({ employees }) => {
        const mgrs = (employees || []).filter(
          (e) => (e.role || "").toLowerCase() === "manager",
        );
        setManagers(mgrs);
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setError("");
    if (!name.trim()) return setError("Department name is required.");
    setSubmitting(true);
    try {
      const result = await apiCall({
        name: name.trim(),
        managerId: managerId || null,
      });
      onSaved && onSaved(result);
      onClose();
    } catch (err) {
      if (err.message === "duplicate_name")
        setError("A department with this name already exists.");
      else if (err.message === "manager_already_assigned")
        setError("This manager already manages another department.");
      else if (err.message === "manager_not_found")
        setError("Selected manager not found.");
      else setError(err.message || "Could not save department.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="modal-head">
        <h3>{initial ? "Edit Department" : "New Department"}</h3>
        <button
          type="button"
          className="icon-btn"
          onClick={onClose}
          disabled={submitting}
        >
          <Icon name="x" size={16} />
        </button>
      </div>
      <div
        className="modal-body"
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        {error && (
          <div
            style={{
              background: "rgba(239,68,68,.1)",
              border: "1px solid var(--error)",
              color: "var(--error)",
              padding: "10px 12px",
              borderRadius: 8,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}
        <div style={deptFieldStyle}>
          <label style={deptLabelStyle}>Name</label>
          <input
            style={deptInputStyle(false)}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Engineering"
            autoFocus={focus !== "manager"}
            disabled={submitting}
          />
        </div>
        <div style={deptFieldStyle}>
          <label style={deptLabelStyle}>Manager (optional)</label>
          <select
            style={deptInputStyle(false)}
            value={managerId}
            onChange={(e) => setManagerId(e.target.value)}
            disabled={submitting}
            autoFocus={focus === "manager"}
          >
            <option value="">— No manager yet —</option>
            {managers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
                {m.department && m.department !== "—" && m.department !== name
                  ? ` (currently: ${m.department})`
                  : ""}
              </option>
            ))}
          </select>
          {managers.length === 0 && (
            <span style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
              No managers yet — add one from the Managers page.
            </span>
          )}
        </div>
      </div>
      <div className="modal-foot">
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={submitting}
        >
          {submitting ? "Saving..." : submitLabel}
        </button>
      </div>
    </form>
  );
}

export function NewDepartmentModal({ onClose, onCreated }) {
  return (
    <DepartmentForm
      onClose={onClose}
      onSaved={onCreated}
      submitLabel="Create department"
      apiCall={createDepartment}
    />
  );
}

export function EditDepartmentModal({ dept, focus, onClose, onSaved }) {
  return (
    <DepartmentForm
      initial={dept}
      focus={focus}
      onClose={onClose}
      onSaved={onSaved}
      submitLabel="Save changes"
      apiCall={(body) => updateDepartment(dept.id, body)}
    />
  );
}

export function ConfirmDeleteDepartment({ dept, onClose, onDeleted }) {
  const [error, setError] = useState("");
  const [blockedCount, setBlockedCount] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleDelete = async () => {
    setError("");
    setSubmitting(true);
    try {
      await deleteDepartment(dept.id);
      onDeleted && onDeleted();
      onClose();
    } catch (err) {
      if (err.message === "in_use") {
        setBlockedCount(err.details?.count ?? null);
        setError(
          `Cannot delete — there are still users assigned to "${dept.name}". Reassign them first.`,
        );
      } else {
        setError(err.message || "Could not delete department.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="modal-head">
        <h3>Delete department?</h3>
        <button
          type="button"
          className="icon-btn"
          onClick={onClose}
          disabled={submitting}
        >
          <Icon name="x" size={16} />
        </button>
      </div>
      <div
        className="modal-body"
        style={{ display: "flex", flexDirection: "column", gap: 12 }}
      >
        {error ? (
          <div
            style={{
              background: "rgba(239,68,68,.1)",
              border: "1px solid var(--error)",
              color: "var(--error)",
              padding: "10px 12px",
              borderRadius: 8,
              fontSize: 13,
            }}
          >
            {error}
            {blockedCount != null && (
              <div style={{ marginTop: 6, fontSize: 12 }}>
                {blockedCount} user{blockedCount === 1 ? "" : "s"} still
                reference this department.
              </div>
            )}
          </div>
        ) : (
          <p style={{ margin: 0, color: "var(--text)" }}>
            Are you sure you want to delete{" "}
            <strong>"{dept.name}"</strong>? This cannot be undone.
          </p>
        )}
      </div>
      <div className="modal-foot">
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          style={{ background: "var(--error)" }}
          onClick={handleDelete}
          disabled={submitting || blockedCount != null}
        >
          {submitting ? "Deleting..." : "Delete"}
        </button>
      </div>
    </>
  );
}
